import { useEffect, useRef, useState } from 'react';
import { cn } from '@core/ui';
import { useAutoGrow } from './useAutoGrow';

export interface InlineEditTextProps {
  value: string;
  /** Called with the trimmed new value when it differs from the current one. */
  onCommit: (next: string) => void;
  /** Shown muted when the value is empty ("Add description…"). */
  placeholder?: string;
  /** `title` renders large/semibold for entity titles; `text` is body-sized. */
  variant?: 'text' | 'title';
  /** Empty commits clear the value; off by default (empty reverts). */
  allowEmpty?: boolean;
  className?: string;
  'aria-label'?: string;
}

/**
 * Click-to-edit text — the inline-edit primitive for detail surfaces and
 * drawers. Click the value → same-size editor in place; Enter/blur commits,
 * Esc reverts. No edit mode, no pencil icon, no Save button.
 * See docs/ux/14-forms.md.
 */
export function InlineEditText({
  value,
  onCommit,
  placeholder,
  variant = 'text',
  allowEmpty = false,
  className,
  'aria-label': ariaLabel,
}: InlineEditTextProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const committedRef = useRef(false);
  const inputRef = useAutoGrow(editing ? draft : '');

  useEffect(() => {
    if (!editing) return;
    const el = inputRef.current;
    el?.focus();
    el?.select();
  }, [editing, inputRef]);

  const startEditing = () => {
    setDraft(value);
    committedRef.current = false;
    setEditing(true);
  };

  const finish = (save: boolean) => {
    if (committedRef.current) return;
    committedRef.current = true;
    setEditing(false);
    if (!save) return;
    const next = draft.trim();
    if (next === value.trim()) return;
    if (!next && !allowEmpty) return;
    onCommit(next);
  };

  const textClass =
    variant === 'title' ? 'text-lg font-semibold leading-snug' : 'text-sm leading-relaxed';

  if (editing) {
    return (
      <textarea
        ref={inputRef}
        rows={1}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => finish(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            finish(true);
          } else if (e.key === 'Escape') {
            e.stopPropagation();
            finish(false);
          }
        }}
        autoCapitalize="off"
        aria-label={ariaLabel || placeholder}
        className={cn(
          '-mx-1.5 w-[calc(100%+12px)] resize-none overflow-hidden rounded-md bg-accent/70 px-1.5 py-0.5',
          'text-foreground focus:outline-none focus:ring-2 focus:ring-ring',
          textClass,
          className
        )}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={startEditing}
      aria-label={ariaLabel || placeholder}
      className={cn(
        '-mx-1.5 w-[calc(100%+12px)] rounded-md px-1.5 py-0.5 text-left hover:bg-accent/70',
        textClass,
        !value && 'text-muted-foreground/70',
        className
      )}
    >
      {value || placeholder}
    </button>
  );
}
